// Display names and plain-words explanations for the Voice engines page.

import type { ClientInfo, Decision, EngineName } from "../../services/voiceEngines";

export const ENGINE_LABEL: Record<EngineName, string> = {
  gemini_live: "Gemini Live",
  elevenlabs: "ElevenLabs",
  cascade: "Cascade",
};

// The order engines are listed in (selects, profile groups).
export const ENGINE_ORDER: EngineName[] = ["gemini_live", "elevenlabs", "cascade"];

export function engineLabel(engine: string): string {
  return ENGINE_LABEL[engine as EngineName] ?? engine;
}

// Experiment, arm and profile keys (app/models/voice_engine.py KEY_PATTERN).
export const KEY_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;
export const KEY_RULE = "letters, digits, '.', '_' or '-', up to 64, starting with a letter or digit";

// What decided an engine (`engine_source`), in plain words.
const SOURCE_TEXT: Record<string, string> = {
  kill_switch: "kill switch off",
  keyboard: "Keyboard Mode",
  override: "Studio test user",
  override_env: "env test user (deprecated)",
  policy_off: "policy off",
  client_build: "build below the floor",
  bucket: "experiment bucket",
  default: "outside every arm",
};

export function sourceText(source: string): string {
  return SOURCE_TEXT[source] ?? source;
}

// One line on why a decision is what it is, e.g. "arm live" or
// "elevenlabs not allowed here: cascade".
export function decisionWhy(d: Decision): string {
  const base = d.source === "bucket" && d.arm ? `arm ${d.arm}` : sourceText(d.source);
  if (d.reason === "voice_engine_not_allowed") {
    return `${base} · ${d.requestedEngine ?? "the engine"} is not allowed here, so the cascade`;
  }
  if (d.reason === "voice_engine_profile_missing") {
    return `${base} · profile ${d.requestedProfile ?? "?"} is missing, so the built-in default`;
  }
  return base;
}

// The profile a decision runs, e.g. "live-default (built-in)" or "el-eager v3".
export function profileText(key: string, version: number): string {
  return version === 0 ? `${key} (built-in)` : `${key} v${version}`;
}

// Arm colours, in arm order (the cascade's rest is --panel-3).
const ARM_COLORS = ["var(--accent)", "var(--mixed)", "var(--warn)", "var(--accent-2)"];

export function armColor(index: number): string {
  return ARM_COLORS[index % ARM_COLORS.length];
}

const PLATFORM: Record<string, string> = { ios: "iOS", android: "Android", legacy: "legacy client" };

// "iOS build 1412 (latest seen)", "legacy client".
export function clientText(c: ClientInfo): string {
  const where = c.platform === "legacy" ? PLATFORM.legacy : `${PLATFORM[c.platform]} build ${c.build ?? "?"}`;
  return c.fromSighting ? `${where} (latest seen)` : where;
}
